/**
 * Service interfaces for dependency injection in the Provet booking system.
 *
 * These interfaces allow for:
 * - Easy testing with mock implementations
 * - Flexibility to swap implementations
 * - Clear separation of concerns
 * - Better testability without actual DOM
 */

/**
 * Abstracts DOM operations for testability.
 */
export interface DOMService {
  /**
   * Queries the document for elements matching a selector.
   * @param selector - CSS selector string
   * @returns Array of matching elements
   */
  querySelectorAll(selector: string): Element[];

  /**
   * Gets an element by its ID.
   * @param id - Element ID
   * @returns Element or null if not found
   */
  getElementById(id: string): HTMLElement | null;

  /**
   * Gets an attribute value from an element.
   * @param element - DOM element
   * @param attributeName - Attribute name
   * @returns Attribute value or null
   */
  getAttribute(element: Element, attributeName: string): string | null;

  /**
   * Sets an attribute on an element.
   * @param element - DOM element
   * @param attributeName - Attribute name
   * @param value - Attribute value
   */
  setAttribute(element: Element, attributeName: string, value: string): void;

  /**
   * Adds an event listener to the document.
   * @param event - Event name
   * @param handler - Event handler function
   */
  addEventListener(event: string, handler: () => void): void;
}

/**
 * Abstracts React rendering for testability.
 */
export interface ReactRendererService {
  /**
   * Renders a React component into a DOM element.
   * @param component - React component to render
   * @param container - DOM element to render into
   */
  render(component: React.ReactElement, container: Element): void;
}

/**
 * Configuration for bootstrapping the Provet booking system.
 */
export interface BootstrapConfig {
  /** CSS selector for component containers */
  componentSelector: string;
  /** Data attribute name for component type */
  componentAttribute: string;
  /** Expected component type value */
  componentType: string;
  /** Data attribute name for props */
  propsAttribute: string;
}

/**
 * Service for bootstrapping React components in the DOM.
 */
export interface BootstrapService {
  /**
   * Initializes and renders React components found in the DOM.
   * @param config - Bootstrap configuration
   */
  initialize(config: BootstrapConfig): void;
}

/**
 * Default bootstrap configuration for Provet booking.
 */
export const DEFAULT_BOOTSTRAP_CONFIG: BootstrapConfig = {
  componentSelector: "[data-react-component=ProvetBookingDialog]",
  componentAttribute: "data-react-component",
  componentType: "ProvetBookingDialog",
  propsAttribute: "data-props"
};
